import type { ScamTemplate, ScamType } from "./lib/types";

export type Locale = "zh" | "en";

export function normalizeLocale(value: string | null | undefined): Locale {
  return value?.toLowerCase().startsWith("en") ? "en" : "zh";
}

export function getInitialLocale(): Locale {
  const query = new URLSearchParams(window.location.search).get("lang");
  const stored = window.localStorage.getItem("rug-rumble-locale");
  return normalizeLocale(query || stored || navigator.language || "zh");
}

export const TEXT = {
  zh: {
    eyebrow: "Monad Blitz · Agentic X",
    tagline: "AI Agent 在你签名前，先替你跟骗局打一架。",
    nav: {
      arena: "Arena 大屏",
      spawn: "观众端",
      admin: "Admin",
      market: "Intel Market"
    },
    language: "English",
    status: {
      contract: "合约",
      rpc: "RPC",
      step: "Step",
      x402: "x402",
      block: "区块",
      checking: "检查中",
      notSet: "未配置"
    },
    sections: {
      spawn: "选择战场",
      liveBattle: "实时对战",
      monadStream: "Monad 证据流",
      reputation: "声誉榜",
      dialogue: "Agent 对话",
      proof: "证明链接",
      x402Stream: "x402 支付流",
      whiteBrain: "White Agent Brain",
      wallet: "钱包连接",
      admin: "Demo 控制台",
      market: "风险情报市场"
    },
    buttons: {
      judge: "让 AI 判定",
      storm: "开启 Rug Storm",
      ask: "提问",
      connectWallet: "连接钱包",
      switchMonad: "切换 Monad",
      smoke: "x402 Smoke Test",
      watchArena: "回到 Arena",
      fundIntel: "增加预算",
      starveAgent: "削减预算"
    },
    chips: {
      casePending: "等待案件",
      caseNo: "案件",
      safe: "安全",
      risk: "风险"
    },
    agents: {
      blackTitle: "黑方 Agent",
      blackLabel: "诱饵文案官",
      whiteTitle: "白方 Agent",
      whiteLabel: "钱包保镖",
      disguise: "伪装度",
      risk: "风险"
    },
    caseFields: {
      displayedIntent: "页面承诺",
      actualAction: "真实动作",
      hiddenPayload: "隐藏载荷"
    },
    metrics: {
      solved: "已判案件",
      stopped: "阻断威胁",
      accuracy: "准确率",
      latency: "平均延迟",
      paidCalls: "付费情报",
      spend: "Agent 花费"
    },
    leaderboard: {
      whiteAgent: "白方 Agent",
      stopped: "已阻断",
      wallet: "钱包"
    },
    proof: {
      arena: "竞技场合约",
      latest: "最新案件交易",
      storm: "风暴批处理交易",
      relayer: "Relayer"
    },
    x402: {
      headline: "这个 AI 不只是分析。它会自己花钱买情报。",
      budget: "预算",
      baseRisk: "本地风险",
      confidence: "置信度",
      plan: "购买计划",
      spent: "已花费",
      remaining: "剩余",
      noPayment: "安全 case 置信度足够，本轮不购买情报。",
      request: "请求风险 API",
      required: "HTTP 402 Payment Required",
      signing: "Agent 钱包签名付款",
      paid: "付款已验证",
      unlocked: "情报已解锁",
      failed: "x402 失败，已明确显示错误",
      receipt: "链上支付证据",
      smokeReady: "未运行",
      smokeOk: "402 → payment → intel unlocked 全链路通过",
      smokeFailed: "x402 smoke test 失败"
    },
    wallet: {
      title: "Presenter Wallet Mode",
      guest: "观众端默认 Guest Mode；White Agent 使用后端 Agent 钱包购买 x402 情报。",
      connected: "已连接",
      disconnected: "未连接",
      noProvider: "未检测到浏览器钱包",
      chain: "链",
      address: "地址"
    },
    admin: {
      subtitle: "演示前先跑 smoke test，再触发预设 case 或 Rug Storm。",
      presetAirdrop: "预设：假空投",
      presetApproval: "预设：无限授权",
      presetHoneypot: "预设：蜜罐",
      storm5: "Rug Storm 5",
      realMode: "Real x402 Mode",
      demoMode: "Fallback Mode 会明确标注，不假装真实付款。"
    },
    spawnView: {
      subtitle: "10 秒内把任意骗局或正常交易送进竞技场。",
      customTitle: "AI 自定义生成",
      customSubtitle: "输入你想看的场景，Step Agent 会生成一个新的交易 case，再交给 White Agent 判定。",
      customScam: "生成骗局",
      customSafe: "生成正常交易",
      customPlaceholderScam: "例如：一个假质押收益页面，声称领取收益，但真实签名会给陌生合约无限授权",
      customPlaceholderSafe: "例如：一个正常 LP 退出交易，金额和接收地址都明确，没有额外授权",
      scamIdeas: ["假质押收益领取", "仿 Monad 域名空投", "假 NFT 白名单铸造"],
      safeIdeas: ["正常 LP 退出", "限额 Swap", "领取已验证徽章"],
      customGenerate: "AI 生成并判定",
      presetTitle: "或者直接选择代表 case",
      budget: "Agent 预算",
      normal: "正常 0.005 USDC",
      starve: "饿它一下 0.001 USDC",
      fund: "加钱买情报 0.010 USDC",
      done: "你的骗局已经进入竞技场。去看 AI 花钱抓它。"
    },
    judgeModal: {
      runningButton: "AI 判定中",
      runningTitle: "AI 正在审判这个骗局",
      runningBody: "White Agent 正在读取真实动作、检查本地规则，并按预算决定是否用 x402 购买风险情报。",
      doneTitle: "判定完成",
      errorTitle: "判定失败",
      stepAgent: "Step Agent 对话",
      x402Budget: "x402 情报预算",
      monadProof: "Monad 证据写入",
      intel: "已解锁情报",
      chainTx: "链上证据",
      chainPending: "链上证据生成中",
      correct: "Judge Agent 已确认",
      review: "需要人工复核",
      close: "关闭"
    },
    market: {
      subtitle: "每个风险信号都是一个 API，每个 API 都可以被 Agent 按次付费调用。",
      live: "x402 live",
      demo: "demo",
      planned: "planned",
      future: "未来任何安全数据提供商都能发布 x402 endpoint，让 Agent 发现、付费、消费并评分。"
    },
    chat: {
      intro: "先生成一个案件，再问我为什么放行或阻断。我会用同一条 Step Agent 路径解释判定。",
      defaultQuestion: "为什么白方 Agent 会放行或阻断这笔交易？",
      placeholder: "追问最新案件、攻击载荷或判定理由"
    },
    events: {
      bootArena: "竞技场启动",
      waiting: "等待第一笔案件",
      whiteAgent: "白方 Agent",
      rulesArmed: "规则已上膛：授权、蜜罐、假 mint、安全 claim",
      caseSpawned: "案件生成",
      caseOpened: "CaseOpened",
      agentVerdict: "AgentVerdict",
      paymentEvidence: "PaymentEvidenceLogged",
      reputationUpdated: "ReputationUpdated",
      rugStorm: "Rug Storm",
      stormSpawning: "5 个代表 case 同时冲进竞技场",
      batchSettled: "批量结算",
      blocked: "已阻断",
      monadEventStream: "Monad 事件流",
      arenaError: "竞技场错误",
      spawnFailed: "案件生成失败",
      stormFailed: "风暴结算失败",
      chatStep: "Step Agent 对话",
      chatLive: "实时 Step 回答",
      chatFallback: "规则兜底回答"
    },
    errors: {
      config: "配置加载失败",
      spawn: "案件生成失败",
      storm: "Rug Storm 失败",
      chat: "Agent 对话失败",
      wallet: "钱包连接失败",
      x402: "x402 支付失败"
    }
  },
  en: {
    eyebrow: "Monad Blitz · Agentic X",
    tagline: "AI agents buy risk intel with x402 before you sign.",
    nav: {
      arena: "Arena",
      spawn: "Spawn",
      admin: "Admin",
      market: "Intel Market"
    },
    language: "中文",
    status: {
      contract: "Contract",
      rpc: "RPC",
      step: "Step",
      x402: "x402",
      block: "block",
      checking: "checking",
      notSet: "not set"
    },
    sections: {
      spawn: "Spawn Case",
      liveBattle: "Live Battle",
      monadStream: "Monad Stream",
      reputation: "Reputation",
      dialogue: "Agent Dialogue",
      proof: "Proof Links",
      x402Stream: "x402 Payment Stream",
      whiteBrain: "White Agent Brain",
      wallet: "Wallet Connect",
      admin: "Demo Control",
      market: "Risk Intel Market"
    },
    buttons: {
      judge: "Let AI Judge",
      storm: "Start Rug Storm",
      ask: "Ask",
      connectWallet: "Connect Wallet",
      switchMonad: "Switch Monad",
      smoke: "x402 Smoke Test",
      watchArena: "Watch Arena",
      fundIntel: "Fund Intel",
      starveAgent: "Starve Agent"
    },
    chips: {
      casePending: "Case pending",
      caseNo: "Case",
      safe: "SAFE",
      risk: "RISK"
    },
    agents: {
      blackTitle: "Black Agent",
      blackLabel: "Scam Crafter",
      whiteTitle: "White Agent",
      whiteLabel: "Wallet Bodyguard",
      disguise: "Disguise",
      risk: "Risk"
    },
    caseFields: {
      displayedIntent: "Displayed intent",
      actualAction: "Actual action",
      hiddenPayload: "Hidden payload"
    },
    metrics: {
      solved: "Cases Solved",
      stopped: "Threats Stopped",
      accuracy: "Accuracy",
      latency: "Avg Latency",
      paidCalls: "Paid Intel",
      spend: "Agent Spend"
    },
    leaderboard: {
      whiteAgent: "White Agent",
      stopped: "Stopped",
      wallet: "Wallet"
    },
    proof: {
      arena: "Arena contract",
      latest: "Latest case tx",
      storm: "Storm batch tx",
      relayer: "Relayer"
    },
    x402: {
      headline: "This AI doesn't just analyze. It pays for the intel it needs.",
      budget: "Budget",
      baseRisk: "Base Risk",
      confidence: "Confidence",
      plan: "Purchase Plan",
      spent: "Spent",
      remaining: "Remaining",
      noPayment: "Safe case confidence is high enough; no paid intel needed.",
      request: "Request risk API",
      required: "HTTP 402 Payment Required",
      signing: "Agent wallet signs payment",
      paid: "Payment verified",
      unlocked: "Intel unlocked",
      failed: "x402 failed; error shown honestly",
      receipt: "On-chain payment evidence",
      smokeReady: "Not run",
      smokeOk: "402 -> payment -> intel unlocked passed",
      smokeFailed: "x402 smoke test failed"
    },
    wallet: {
      title: "Presenter Wallet Mode",
      guest: "Audience uses Guest Mode by default; White Agent buys x402 intel with the backend agent wallet.",
      connected: "Connected",
      disconnected: "Disconnected",
      noProvider: "No browser wallet detected",
      chain: "Chain",
      address: "Address"
    },
    admin: {
      subtitle: "Run smoke test first, then trigger preset cases or Rug Storm.",
      presetAirdrop: "Preset: Fake Airdrop",
      presetApproval: "Preset: Approval Drain",
      presetHoneypot: "Preset: Honeypot",
      storm5: "Rug Storm 5",
      realMode: "Real x402 Mode",
      demoMode: "Fallback Mode is labeled honestly; no fake payments."
    },
    spawnView: {
      subtitle: "Send any scam or normal transaction into the arena in 10 seconds.",
      customTitle: "AI Custom Generator",
      customSubtitle: "Describe the scene you want. Step Agent generates a new transaction case before White Agent judges it.",
      customScam: "Generate Scam",
      customSafe: "Generate Normal Tx",
      customPlaceholderScam: "Example: a fake staking rewards page that claims to harvest yield but signs unlimited approval to an unknown contract",
      customPlaceholderSafe: "Example: a normal LP withdrawal with explicit amount and recipient, no extra approval",
      scamIdeas: ["Fake staking reward", "Monad lookalike airdrop", "Fake NFT allowlist mint"],
      safeIdeas: ["Normal LP exit", "Limited swap", "Verified badge claim"],
      customGenerate: "Generate & Judge",
      presetTitle: "Or pick a representative case",
      budget: "Agent Budget",
      normal: "Normal 0.005 USDC",
      starve: "Starve it 0.001 USDC",
      fund: "Fund intel 0.010 USDC",
      done: "Your scam entered the arena. Watch the AI spend money to catch it."
    },
    judgeModal: {
      runningButton: "Judging",
      runningTitle: "AI is judging this case",
      runningBody: "The White Agent is reading the actual action, checking local rules, and deciding whether to buy x402 risk intel within budget.",
      doneTitle: "Judgement complete",
      errorTitle: "Judgement failed",
      stepAgent: "Step Agent dialogue",
      x402Budget: "x402 intel budget",
      monadProof: "Monad proof write",
      intel: "Unlocked intel",
      chainTx: "On-chain proof",
      chainPending: "On-chain proof pending",
      correct: "Judge Agent confirmed",
      review: "Needs human review",
      close: "Close"
    },
    market: {
      subtitle: "Every risk signal is an API. Every API can be paid for by an agent.",
      live: "x402 live",
      demo: "demo",
      planned: "planned",
      future: "Future: any security provider can publish an x402 endpoint for agents to discover, pay, consume, and rate."
    },
    chat: {
      intro:
        "Spawn a case, then ask why it was allowed or blocked. I will answer with the same Step-powered agent path used by the arena.",
      defaultQuestion: "Why did the White Agent allow or block this transaction?",
      placeholder: "Ask the agents about the latest case"
    },
    events: {
      bootArena: "Arena boot",
      waiting: "Waiting for first case",
      whiteAgent: "White Agent",
      rulesArmed: "Rules armed: approvals, honeypots, mint traps, safe claims",
      caseSpawned: "Case spawned",
      caseOpened: "CaseOpened",
      agentVerdict: "AgentVerdict",
      paymentEvidence: "PaymentEvidenceLogged",
      reputationUpdated: "ReputationUpdated",
      rugStorm: "Rug Storm",
      stormSpawning: "Spawning 5 representative scam cases",
      batchSettled: "Batch settled",
      blocked: "blocked",
      monadEventStream: "Monad event stream",
      arenaError: "Arena error",
      spawnFailed: "Spawn flow failed",
      stormFailed: "Storm flow failed",
      chatStep: "Step agent chat",
      chatLive: "Live Step response",
      chatFallback: "Step API error"
    },
    errors: {
      config: "Failed to load config",
      spawn: "Spawn failed",
      storm: "Storm failed",
      chat: "Agent chat failed",
      wallet: "Wallet connection failed",
      x402: "x402 payment failed"
    }
  }
} as const;

const TEMPLATE_TEXT: Record<Locale, Record<ScamType, Pick<ScamTemplate, "label" | "shortLabel" | "displayedIntent" | "calldataAction" | "hiddenPayload" | "riskFlags">>> = {
  zh: {
    FakeAirdrop: {
      label: "假空投领取",
      shortLabel: "空投",
      displayedIntent: "领取 10,000 MON 建设者奖励",
      calldataAction: "approve(spender, uint256.max)",
      hiddenPayload: "给陌生地址无限代币授权",
      riskFlags: ["无限授权", "意图不一致", "陌生 spender"]
    },
    ApprovalDrain: {
      label: "无限授权钓鱼",
      shortLabel: "授权",
      displayedIntent: "验证钱包以获得 Monad Blitz 访问资格",
      calldataAction: "setApprovalForAll(attacker, true)",
      hiddenPayload: "伪装成验证的整套藏品 operator 授权",
      riskFlags: ["operator 授权", "钓鱼验证", "资产被搬空路径"]
    },
    FakeMint: {
      label: "假 NFT Mint",
      shortLabel: "Mint",
      displayedIntent: "铸造 Monad Blitz 稀有 NFT",
      calldataAction: "向未知金库转原生代币费用",
      hiddenPayload: "页面收取费用但没有 mint 或分配 NFT",
      riskFlags: ["只收费不 mint", "没有 token 分配", "未知金库"]
    },
    Honeypot: {
      label: "蜜罐代币",
      shortLabel: "蜜罐",
      displayedIntent: "在 Monad 上线前抢购早期代币",
      calldataAction: "buy() 成功，但非 owner 调用 sell() 会回滚",
      hiddenPayload: "能买不能卖，普通用户无法退出",
      riskFlags: ["卖出被拦截", "owner-only 转账", "流动性陷阱"]
    },
    SafeClaim: {
      label: "真实奖励领取",
      shortLabel: "安全领取",
      displayedIntent: "领取已验证 Monad 建设者徽章",
      calldataAction: "claim(address recipient)，无代币授权",
      hiddenPayload: "无授权、无付款、recipient 匹配当前钱包",
      riskFlags: ["已验证合约", "无代币移动", "接收地址匹配钱包"]
    },
    SafeSwap: {
      label: "正常限额 Swap",
      shortLabel: "安全 Swap",
      displayedIntent: "用 5 MON 按报价换取目标代币",
      calldataAction: "swapExactNativeForTokens，带滑点上限",
      hiddenPayload: "预期 router、金额有界、没有无限授权",
      riskFlags: ["已知 router", "滑点有界", "无无限授权"]
    }
  },
  en: {
    FakeAirdrop: {
      label: "Fake Airdrop Claim",
      shortLabel: "Airdrop",
      displayedIntent: "Claim 10,000 MON builder reward",
      calldataAction: "approve(spender, uint256.max)",
      hiddenPayload: "Unlimited token approval to an unknown spender",
      riskFlags: ["unlimited approval", "intent mismatch", "unknown spender"]
    },
    ApprovalDrain: {
      label: "Unlimited Approval Drain",
      shortLabel: "Approval",
      displayedIntent: "Verify wallet for Monad Blitz access",
      calldataAction: "setApprovalForAll(attacker, true)",
      hiddenPayload: "Collection-wide operator approval disguised as verification",
      riskFlags: ["operator approval", "phishing verification", "asset drain path"]
    },
    FakeMint: {
      label: "Fake NFT Mint",
      shortLabel: "Mint",
      displayedIntent: "Mint Monad Blitz Rare NFT",
      calldataAction: "transfer native fee to unknown treasury",
      hiddenPayload: "Mint page charges a fee but does not mint or assign an NFT",
      riskFlags: ["fee-only mint", "no token assignment", "unknown treasury"]
    },
    Honeypot: {
      label: "Honeypot Token",
      shortLabel: "Honeypot",
      displayedIntent: "Buy early token before Monad listing",
      calldataAction: "buy() succeeds, sell() reverts for non-owner",
      hiddenPayload: "Token can be bought but normal users cannot exit",
      riskFlags: ["sell blocked", "owner-only transfer", "liquidity trap"]
    },
    SafeClaim: {
      label: "Legit Reward Claim",
      shortLabel: "Safe Claim",
      displayedIntent: "Claim verified Monad builder badge",
      calldataAction: "claim(address recipient) with no token approval",
      hiddenPayload: "No approval, no payment, recipient matches connected wallet",
      riskFlags: ["verified contract", "no token movement", "recipient matches wallet"]
    },
    SafeSwap: {
      label: "Normal Limited Swap",
      shortLabel: "Safe Swap",
      displayedIntent: "Swap 5 MON for a quoted token amount",
      calldataAction: "swapExactNativeForTokens with bounded slippage",
      hiddenPayload: "Expected router, bounded amount, no unlimited approval",
      riskFlags: ["known router", "bounded slippage", "no unlimited approval"]
    }
  }
};

export function localizeTemplate(template: ScamTemplate, locale: Locale): ScamTemplate {
  if (template.generated) return template;
  return { ...template, ...TEMPLATE_TEXT[locale][template.id] };
}
